import React, { Fragment, useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRealtimeTags } from 'Core/hooks';
import { useIntl } from "react-intl";

// Global Components
import Modal, { ModalBody, ModalFooter } from "Components/Modal";
import Tabs, { TabContainer } from 'Components/Tabs';
import { NotificationManager } from 'Components/Notifications';
import { Form, Button, Select } from "Components";
import { Grid, Box, Chip, Icon } from '@mui/material';

//Controls
import VehicleStatusIconPicker from "../vehicleStatusIconPicker"
import { findColor } from 'Components/Map/utils';
import { upsertManyWithRules as upsertRealtimeUnits } from 'Core/realtime/redux/unitSlice';

// Actions
import {
  unitEditUnitClose,
  catalogLoad,
  updateUnitGroupTagAssignment,
  catalogProcessItems,
  clearRedux
} from "Redux/actions";

// Components
import TagsManage from "Modules/.common/components/TagsManage";

import './styles.scss';

const EditMultipleUnits = (props) => {
  const formUseRef = useRef();
  const dispatch = useDispatch();
  const { messages } = useIntl();
  const { unitFiltered, clearItemsSelected } = props;
  // Local State
  const [formData, setFormData] = useState({});
  const [tagsChanged, setTagsChanged] = useState([]);
  const [iconChanged, setIconChanged] = useState();
  const [errorMessage, setErrorMessage] = useState();
  // Global State
  const showEditMultipleUnits = useSelector(state => state.editUnitRedux.showEditMultipleUnits);
  const units = useSelector(state => state.editUnitRedux.units);
  const loading = useSelector(state => state.unitSettingsRedux.loading);
  const updateUnits = useSelector(state => state.unitSettingsRedux.updateUnits);
  const tags = useRealtimeTags({ trackFields: ['id', 'label', 'disabled', 'tagPath', 'tags'] });
  const vehicleStatus = useSelector(state => state.catalogsRedux.vehicleStatus);

  useEffect(() => {
    return () => {
      setFormData({});
      setTagsChanged([]);
      setIconChanged();
      setErrorMessage();
    }
  }, []);

  useEffect(() => {
    if (updateUnits && showEditMultipleUnits) {
      NotificationManager.success(
        messages['unit_settings_update_groups'],
        messages['success'],
        3000,
        () => dispatch(openDriverTransactionModal(true)),
        null,
        'success',
        null
      );


      let newUnits = units.map(newUnit => {
        let unitTags = Array.isArray(newUnit?.tags) ? [...newUnit.tags] : [];
        let unitIcon = newUnit.icon;

        let unitTagIds = unitTags.map(ut => ut.id);

        tagsChanged.forEach(tagChanged => {
          if (tagChanged?.entityIds?.includes(newUnit?.id)) {
            if (tagChanged.action === "Create" && !unitTagIds.includes(tagChanged.tagId)) {
              const tag = tags.find(t => t.id == tagChanged.tagId);
              if (tag) unitTags.push(tag);
            } else if (tagChanged.action === "Delete") {
              unitTags = unitTags.filter(t => t.id != tagChanged.tagId);
            }
          }
        });

        unitIcon = iconChanged?.iconId > 0 ? iconChanged.iconId : 0;


        return {
          ...newUnit,
          id: newUnit.id,
          CUnits_id: newUnit.id,
          tags: unitTags,
          vehicleStatusId: unitIcon
        };
      });

      dispatch(catalogProcessItems("Unit", newUnits));

      dispatch(
        upsertRealtimeUnits(
          newUnits.map(({ id, tags, vehicleStatusId }) => ({
            id,
            tags,
            vehicleStatusId
          }))
        )
      );

      setTimeout(() => {
        handleClose();
        clearItemsSelected();
      }, 200);
    }
  }, [updateUnits, showEditMultipleUnits])

  useEffect(() => {
    if (units && tags && showEditMultipleUnits) {
      let selectedTags = [];
      tags.forEach(tag => {
        let newTag = { ...tag, color: `#${tag.color}`, units: [] };
        units.forEach(unit => {
          unit?.tags?.forEach(tag => {
            if (newTag.id == tag.id) {
              newTag.units.push(unit);
            }
          });
        });

        selectedTags.push({
          ...newTag,
          label: newTag?.units?.length ? `${newTag.label} (${newTag?.units?.length})` : newTag.label,
          countUnits: newTag?.units?.length
        });
      });

      setFormData({
        tags: selectedTags.filter(selectedTag => selectedTag.units.length),
        vehicleStatusIcon: selectFirstImageFromUnits(units),
        vehicleStatusIconData: selectFirstImageFromUnits(units, true)
      });

    }
  }, [units, tags, showEditMultipleUnits])

  const onSubmit = () => {
      let data = [];
      let unitIds = units.map(unit => unit.id);

      // Build a map: tagId → which of the selected units currently have it
      let currentAssignments = {};
      units.forEach((unit) => {
          unit?.tags?.forEach(tag => {
              if (!currentAssignments[tag?.id]) {
                  currentAssignments[tag?.id] = [];
              }
              currentAssignments[tag?.id].push(unit.id);
          });
      });

      let selectedTagIds = formData?.tags?.map(t => t.id) || [];

      if (formData?.tagsChanged) {

          // DELETES: Tags that were on some units but are now deselected
          tags?.forEach(tag => {
              if (!selectedTagIds.includes(tag?.id) && currentAssignments[tag?.id]?.length) {
                  data.push({
                      tagId: tag?.id,
                      entityName: "Unit",
                      // Only send the units that actually had this tag
                      entityIds: currentAssignments[tag?.id],
                      action: "Delete"
                  });
              }
          });

          // CREATES: Tags that the user selected — all units get assigned
          formData?.tags?.forEach(tag => {
              if (tag.countUnits == units.length) {
                  data.push({
                      tagId: tag?.id,
                      entityName: "Unit",
                      entityIds: unitIds,
                      action: "Create"
                  });
              }
          });
      }

      if (data.length || formData?.iconChanged) {
          setTagsChanged(data);

          const dataIcons = {
              updateIcon: units ? true : false,
              unitIds: units ? units.map((item) => item.id) : [],
              iconId: formData?.iconChanged ? formData?.vehicleStatusIconData.id : selectFirstImageFromUnits(units, true)?.id,
              data: formData?.vehicleStatusIconData
          }
          setIconChanged(dataIcons)
          dispatch(updateUnitGroupTagAssignment(data, dataIcons));
      } else {
          handleClose();
      }
  }

  const handleClose = useCallback(() => {
    dispatch(unitEditUnitClose());
    dispatch(clearRedux("MODAL_EDIT_UNIT"))
    dispatch(clearRedux("UNIT_SETTINGS"));
    setTagsChanged([]);
    setFormData({});
    setIconChanged();
  });

  const selectFirstImageFromUnits = (listUnits = [], returnObject = false) => {
    if (listUnits && listUnits?.length > 0) {
      const elementsWithIcon = listUnits.filter((item) => item.icon);
      if (!returnObject) {
        return elementsWithIcon && elementsWithIcon.length > 0 ? elementsWithIcon[0]?.icon?.icon : null;
      }
      return elementsWithIcon && elementsWithIcon.length > 0 ? elementsWithIcon[0]?.icon : { id: 0 };
    }
    return !returnObject ? null : { id: 0 };
  }

  const getTabs = useCallback(() => {
    return [
      {
        icon: "settings",
        label: messages["unit_settings_unit"],
        render: () => {
          return (
            <div className='panelUnitSettings'>
              <Grid display='flex' alignItems='center' container spacing={1}>
                <Grid item sm={6}>
                  <label className='labelUnit'>{messages["unit_settings_modal_icon_type"]}</label>
                </Grid>
                <Grid item sm={6}>
                  <div className={'alignIconVehicleStatus'}>
                    <VehicleStatusIconPicker
                      showDefaulIcon
                      icon={formData?.vehicleStatusIcon}
                      icons={vehicleStatus}
                      //disabled={false}
                      loading={loading}
                      onChange={(value) => {
                        setFormData({
                          ...formData,
                          vehicleStatusIcon: value?.icon,
                          vehicleStatusIconData: value,
                          iconChanged: true
                        });
                      }}
                    />
                  </div>
                </Grid>
              </Grid>
            </div>
          )
        }
      },
      {
        icon: "local_offer",
        label: messages["unit_settings_groups"],
        render: () => {

          return (
            <Box height='calc(100vh - 250px)' overflow='auto'>
              <div
                className="input-normal-label"
                style={{
                  padding: '18px 0px 8px 0px'
                }}
              >
                {messages['unit_settings_belongs_tags']}
              </div>

              <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="flex-start"
                spacing={0}
                style={{ marginTop: 5 }}
              >
                {
                  (formData?.tags && formData?.tags.length > 0) &&
                  formData?.tags.map((v, index) => {
                    let color = findColor(v?.color?.replace('#', ''));
                    return (
                      <Grid item key={index}>
                        <Chip
                          label={v?.label}
                          style={{
                            color: "white",
                            backgroundColor: `#${color}`,
                            marginBottom: 5
                          }}
                          clickable={true}
                          deleteIcon={<Icon style={{ fontSize: 14 }}>close</Icon>}
                          onDelete={() => {
                            let newTags = formData?.tags?.filter(nv => nv.id != v.id);
                            setFormData({ ...formData, tags: newTags, tagsChanged: true });
                          }}
                        />
                      </Grid>
                    )
                  })
                }
              </Grid>
              <div style={{ marginTop: 10}}>
                <TagsManage
                  data-testid="editMultipleUnits-tags"
                  mode="inline"
                  disableUntaggedSelection={true}
                  value={formData?.tags}
                  onChange={(values) => {
                    let newValues = [...values]
                    //Add the number off the vehicles when user select a new element
                    if (formData.tags && newValues && newValues.length > 0 && formData.tags.length < newValues.length) {
                      newValues[newValues.length - 1] = {
                        ...newValues[newValues.length - 1],
                        label: `${newValues[newValues.length - 1].label} (${units.length})`,
                        countUnits: units.length
                      }
                    }
                    let tagChangedFlag = false;
                    if (formData?.tags != newValues) tagChangedFlag = true;
                    setFormData({ ...formData, tags: newValues, tagsChanged: tagChangedFlag });
                    setErrorMessage("");
                  }}
                />
              </div>
              {
                errorMessage &&
                <small className="select-error text-danger">{errorMessage}</small>
              }
              <div style={{ marginTop: 10 }}>
              </div>
            </Box>
          )
        }
      }
    ]
  });

  return (
    <Modal
      title={`${messages["unit_settings_edit_multiple_units"]} (${units?.length} ${messages["unit_settings_units"]})`}
      position="right"
      size="small"
      open={showEditMultipleUnits}
      handleClose={() => handleClose()}
      loading={loading}
    >
      <ModalBody>
        <Box mx={3}>
          <Form ref={formUseRef} onValidSubmit={onSubmit}>
            <Tabs
              tab={0}
              type="icon-indicator"
              border
              onChange={(tab) => { }}
            >
              {getTabs().map((tab, index) => {
                return (
                  <TabContainer key={index} iconIndicator={tab.icon} label={tab.label} index={index} style={{ padding: 0 }}>
                    <Box my={2}>
                      {tab.render()}
                    </Box>
                  </TabContainer>
                )
              })}
            </Tabs>
          </Form>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Button
          className="btn-cancel"
          onClick={() => handleClose()}
        >
          {messages["cancel"]}
        </Button>
        <Button
          className="btn-blue"
          onClick={(e) => {
            e.preventDefault();
            formUseRef.current.submit();
          }}
        >
          {messages["save"]}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default EditMultipleUnits;